import { useState, useEffect, useCallback, useRef } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import {
  getEjercicio,
  getHoldedSumasSaldos,
  getHoldedMayor,
  getHoldedAsientos,
} from "@/lib/api";
import { formatEur } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  ArrowLeft,
  Landmark,
  BookOpen,
  ListOrdered,
  Search,
  Loader2,
  ChevronRight,
} from "lucide-react";

// ---------------------------------------------------------------------------
// Libro contable navegable (estilo SAP) para ejercicios con origen HOLDED.
// Solo LECTURA sobre los apuntes en crudo (`holded_apuntes`): el análisis y la
// proyección siguen alimentándose del Sumas y Saldos importado.
// Navegación circular: Sumas y Saldos → clic en cuenta → MAYOR (con saldo
// acumulado) → clic en asiento → ASIENTO línea a línea → clic en cuenta → mayor.
// ---------------------------------------------------------------------------

export default function ContabilidadHoldedPage() {
  const { ejercicioId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [ejercicio, setEjercicio] = useState(null);
  const [vista, setVista] = useState("sumas");

  // Sumas y Saldos
  const [sumas, setSumas] = useState(null);
  const [filtro, setFiltro] = useState("");

  // Mayor
  const [cuentaMayor, setCuentaMayor] = useState("");
  const [cuentaInput, setCuentaInput] = useState("");
  const [mayor, setMayor] = useState(null);
  const [cargandoMayor, setCargandoMayor] = useState(false);

  // Diario / detalle de asiento
  const [diario, setDiario] = useState(null);
  const [asientoDetalle, setAsientoDetalle] = useState(null);
  const [cargandoAsiento, setCargandoAsiento] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [ej, ss] = await Promise.all([
          getEjercicio(ejercicioId),
          getHoldedSumasSaldos(ejercicioId),
        ]);
        setEjercicio(ej.data);
        setSumas(ss.data);
      } catch (e) {
        toast.error(e.response?.data?.detail || "Error al cargar el libro contable");
        setSumas({ cuentas: [], totales: { debe: 0, haber: 0, saldo: 0 } });
      }
    })();
  }, [ejercicioId]);

  const abrirMayor = useCallback(
    async (cuenta) => {
      const c = String(cuenta || "").trim();
      if (!c) {
        toast.error("Indica una cuenta o un prefijo (p.ej. 705 o 57200000)");
        return;
      }
      setVista("mayor");
      setCuentaMayor(c);
      setCuentaInput(c);
      setCargandoMayor(true);
      try {
        const { data } = await getHoldedMayor(ejercicioId, c);
        setMayor(data);
      } catch (e) {
        toast.error(e.response?.data?.detail || "Error al cargar el mayor");
        setMayor(null);
      } finally {
        setCargandoMayor(false);
      }
    },
    [ejercicioId]
  );

  const cargarDiario = useCallback(async () => {
    try {
      const { data } = await getHoldedAsientos(ejercicioId);
      setDiario(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al cargar el diario");
      setDiario({ total: 0, asientos: [] });
    }
  }, [ejercicioId]);

  const abrirAsiento = useCallback(
    async (numero) => {
      setVista("asientos");
      setCargandoAsiento(true);
      try {
        const { data } = await getHoldedAsientos(ejercicioId, numero);
        setAsientoDetalle(data);
      } catch (e) {
        toast.error(e.response?.data?.detail || "Error al cargar el asiento");
      } finally {
        setCargandoAsiento(false);
      }
    },
    [ejercicioId]
  );

  useEffect(() => {
    if (vista === "asientos" && diario === null) cargarDiario();
  }, [vista, diario, cargarDiario]);

  // Deep-link desde el análisis (navegación tipo SAP): /contabilidad?cuenta=X
  // abre directamente el MAYOR de esa cuenta; ?asiento=N abre ese asiento.
  const deepLinkAplicado = useRef(false);
  useEffect(() => {
    if (deepLinkAplicado.current || sumas === null) return;
    deepLinkAplicado.current = true;
    const cuentaParam = searchParams.get("cuenta");
    const asientoParam = searchParams.get("asiento");
    if (asientoParam && !Number.isNaN(parseInt(asientoParam, 10))) {
      abrirAsiento(parseInt(asientoParam, 10));
    } else if (cuentaParam) {
      abrirMayor(cuentaParam);
    }
  }, [sumas, searchParams, abrirMayor, abrirAsiento]);

  const nombresPorCuenta = {};
  (sumas?.cuentas || []).forEach((c) => {
    nombresPorCuenta[c.cuenta] = c.nombre;
  });

  const cuentasFiltradas = (sumas?.cuentas || []).filter((c) => {
    const f = filtro.trim().toLowerCase();
    if (!f) return true;
    return c.cuenta.startsWith(f) || (c.nombre || "").toLowerCase().includes(f);
  });

  if (ejercicio === null || sumas === null) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  const sinApuntes = (sumas.cuentas || []).length === 0;

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => navigate(`/ejercicios/${ejercicioId}/analisis`)}
        >
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div>
          <h1 className="page-title">
            Contabilidad {ejercicio?.anio}
          </h1>
          <p className="text-sm text-muted-foreground">
            Libro contable del diario de Holded — solo consulta: sumas y saldos, mayor
            por cuenta y asientos. El análisis sigue usando el S&S importado.
          </p>
        </div>
      </div>

      {sinApuntes ? (
        <Alert>
          <AlertDescription>
            Este ejercicio no tiene apuntes de Holded guardados. El libro contable solo
            está disponible en ejercicios sincronizados desde Holded (y el detalle se
            elimina al desconectar la integración).
          </AlertDescription>
        </Alert>
      ) : (
        <Tabs value={vista} onValueChange={setVista} className="w-full">
          <TabsList data-testid="contabilidad-tabs">
            <TabsTrigger value="sumas" className="gap-2" data-testid="tab-libro-sumas">
              <Landmark className="h-4 w-4 hidden sm:block" />
              Sumas y Saldos
            </TabsTrigger>
            <TabsTrigger value="mayor" className="gap-2" data-testid="tab-libro-mayor">
              <BookOpen className="h-4 w-4 hidden sm:block" />
              Mayor
            </TabsTrigger>
            <TabsTrigger value="asientos" className="gap-2" data-testid="tab-libro-asientos">
              <ListOrdered className="h-4 w-4 hidden sm:block" />
              Asientos
            </TabsTrigger>
          </TabsList>

          {/* ─────────────── Sumas y Saldos ─────────────── */}
          <TabsContent value="sumas" className="mt-4">
            <Card>
              <CardHeader className="pb-3">
                <CardTitle>Balance de Sumas y Saldos</CardTitle>
                <CardDescription>
                  Sumas al debe/haber y saldo por cuenta (asientos de cierre excluidos).
                  Haz clic en una cuenta para abrir su mayor.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                <Input
                  value={filtro}
                  onChange={(e) => setFiltro(e.target.value)}
                  placeholder="Filtrar por código o nombre (p.ej. 705, banco…)"
                  className="sm:w-[320px]"
                  data-testid="libro-sumas-filtro"
                />
                <div className="rounded-md border overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-muted/30">
                        <TableHead className="w-[120px]">Cuenta</TableHead>
                        <TableHead>Nombre</TableHead>
                        <TableHead className="text-right w-[130px]">Debe</TableHead>
                        <TableHead className="text-right w-[130px]">Haber</TableHead>
                        <TableHead className="text-right w-[130px]">Saldo</TableHead>
                        <TableHead className="w-[30px]"></TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {cuentasFiltradas.map((c) => (
                        <TableRow
                          key={c.cuenta}
                          className="hover:bg-muted/30 cursor-pointer"
                          onClick={() => abrirMayor(c.cuenta)}
                          data-testid={`libro-sumas-fila-${c.cuenta}`}
                        >
                          <TableCell className="font-mono text-xs">{c.cuenta}</TableCell>
                          <TableCell className="text-xs max-w-[320px] truncate">{c.nombre}</TableCell>
                          <TableCell className="text-right font-mono tabular-nums text-xs">
                            {formatEur(c.debe)}
                          </TableCell>
                          <TableCell className="text-right font-mono tabular-nums text-xs">
                            {formatEur(c.haber)}
                          </TableCell>
                          <TableCell className="text-right font-mono tabular-nums text-xs font-medium">
                            {formatEur(c.saldo)}
                          </TableCell>
                          <TableCell>
                            <ChevronRight className="h-3.5 w-3.5 text-muted-foreground" />
                          </TableCell>
                        </TableRow>
                      ))}
                      <TableRow className="bg-muted/40 font-medium">
                        <TableCell className="text-xs" colSpan={2}>
                          TOTAL ({cuentasFiltradas.length} cuentas
                          {filtro.trim() ? " filtradas" : ""})
                        </TableCell>
                        <TableCell className="text-right font-mono tabular-nums text-xs">
                          {formatEur(cuentasFiltradas.reduce((s, c) => s + c.debe, 0))}
                        </TableCell>
                        <TableCell className="text-right font-mono tabular-nums text-xs">
                          {formatEur(cuentasFiltradas.reduce((s, c) => s + c.haber, 0))}
                        </TableCell>
                        <TableCell className="text-right font-mono tabular-nums text-xs">
                          {formatEur(cuentasFiltradas.reduce((s, c) => s + c.saldo, 0))}
                        </TableCell>
                        <TableCell></TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* ─────────────── Mayor ─────────────── */}
          <TabsContent value="mayor" className="mt-4">
            <Card>
              <CardHeader className="pb-3">
                <CardTitle>
                  Libro mayor
                  {cuentaMayor && (
                    <span className="ml-2 font-mono text-sm text-muted-foreground">
                      {cuentaMayor}
                      {nombresPorCuenta[cuentaMayor] ? ` · ${nombresPorCuenta[cuentaMayor]}` : ""}
                    </span>
                  )}
                </CardTitle>
                <CardDescription>
                  Apuntes de la cuenta en orden cronológico con el saldo acumulado. Haz
                  clic en el nº de asiento para verlo completo. Admite prefijos (705 =
                  todas las 705…).
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex flex-col sm:flex-row gap-3">
                  <Input
                    value={cuentaInput}
                    onChange={(e) => setCuentaInput(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && abrirMayor(cuentaInput)}
                    placeholder="Cuenta o prefijo (p.ej. 57200000 o 705)"
                    className="sm:w-[280px] font-mono"
                    data-testid="libro-mayor-cuenta"
                  />
                  <Button
                    variant="outline"
                    onClick={() => abrirMayor(cuentaInput)}
                    disabled={cargandoMayor || !cuentaInput.trim()}
                    data-testid="libro-mayor-buscar"
                  >
                    {cargandoMayor ? (
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <Search className="h-4 w-4 mr-2" />
                    )}
                    Abrir mayor
                  </Button>
                </div>

                {mayor && !cargandoMayor && (
                  <>
                    {mayor.apuntes.length === 0 ? (
                      <Alert>
                        <AlertDescription>
                          Sin apuntes para esa cuenta (los asientos de cierre no se
                          muestran en el mayor, igual que en el S&S).
                        </AlertDescription>
                      </Alert>
                    ) : (
                      <div className="rounded-md border overflow-x-auto">
                        <Table>
                          <TableHeader>
                            <TableRow className="bg-muted/30">
                              <TableHead className="w-[100px]">Fecha</TableHead>
                              <TableHead className="w-[90px]">Asiento</TableHead>
                              <TableHead className="w-[120px]">Cuenta</TableHead>
                              <TableHead>Concepto</TableHead>
                              <TableHead className="text-right w-[120px]">Debe</TableHead>
                              <TableHead className="text-right w-[120px]">Haber</TableHead>
                              <TableHead className="text-right w-[130px]">Acumulado</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {mayor.apuntes.map((a, i) => (
                              <TableRow key={i} className="hover:bg-muted/20">
                                <TableCell className="text-xs">{a.fecha}</TableCell>
                                <TableCell className="text-xs tabular-nums">
                                  <button
                                    className="underline decoration-dotted underline-offset-2 hover:text-primary"
                                    onClick={() => abrirAsiento(a.asiento)}
                                    title="Ver el asiento completo"
                                  >
                                    {a.asiento ?? "—"}
                                  </button>
                                </TableCell>
                                <TableCell className="font-mono text-xs">{a.cuenta}</TableCell>
                                <TableCell className="text-xs max-w-[300px] truncate">
                                  {a.concepto || "—"}
                                </TableCell>
                                <TableCell className="text-right font-mono tabular-nums text-xs">
                                  {a.debe ? formatEur(a.debe) : "—"}
                                </TableCell>
                                <TableCell className="text-right font-mono tabular-nums text-xs">
                                  {a.haber ? formatEur(a.haber) : "—"}
                                </TableCell>
                                <TableCell className="text-right font-mono tabular-nums text-xs font-medium">
                                  {formatEur(a.acumulado)}
                                </TableCell>
                              </TableRow>
                            ))}
                            <TableRow className="bg-muted/40 font-medium">
                              <TableCell className="text-xs" colSpan={4}>
                                TOTAL {mayor.truncado ? `(acumulado sobre los ${mayor.total} apuntes)` : ""}
                              </TableCell>
                              <TableCell className="text-right font-mono tabular-nums text-xs">
                                {formatEur(mayor.total_debe)}
                              </TableCell>
                              <TableCell className="text-right font-mono tabular-nums text-xs">
                                {formatEur(mayor.total_haber)}
                              </TableCell>
                              <TableCell className="text-right font-mono tabular-nums text-xs">
                                {formatEur(mayor.saldo_final)}
                              </TableCell>
                            </TableRow>
                          </TableBody>
                        </Table>
                        {mayor.truncado && (
                          <p className="px-4 py-2 text-[11px] text-muted-foreground">
                            Mostrando los primeros {mayor.apuntes.length} de {mayor.total}{" "}
                            apuntes; el saldo final y los totales cubren todos.
                          </p>
                        )}
                      </div>
                    )}
                  </>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* ─────────────── Asientos (diario) ─────────────── */}
          <TabsContent value="asientos" className="mt-4">
            {asientoDetalle ? (
              <Card>
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between gap-3 flex-wrap">
                    <div>
                      <CardTitle>
                        Asiento {asientoDetalle.asiento}
                        <span className="ml-2 text-sm font-normal text-muted-foreground">
                          {asientoDetalle.fecha}
                        </span>
                      </CardTitle>
                      <CardDescription>
                        Haz clic en una cuenta para abrir su mayor.
                      </CardDescription>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setAsientoDetalle(null)}
                      data-testid="libro-asiento-volver"
                    >
                      <ArrowLeft className="h-4 w-4 mr-2" />
                      Volver al diario
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="rounded-md border overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-muted/30">
                          <TableHead className="w-[120px]">Cuenta</TableHead>
                          <TableHead>Nombre / Concepto</TableHead>
                          <TableHead className="text-right w-[130px]">Debe</TableHead>
                          <TableHead className="text-right w-[130px]">Haber</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {asientoDetalle.lineas.map((l, i) => (
                          <TableRow key={i} className="hover:bg-muted/20">
                            <TableCell className="font-mono text-xs">
                              <button
                                className="underline decoration-dotted underline-offset-2 hover:text-primary"
                                onClick={() => abrirMayor(l.cuenta)}
                                title="Ver el mayor de esta cuenta"
                              >
                                {l.cuenta}
                              </button>
                              {l.es_cierre && (
                                <Badge variant="outline" className="ml-1 text-[10px]">cierre</Badge>
                              )}
                            </TableCell>
                            <TableCell className="text-xs max-w-[360px] truncate">
                              {nombresPorCuenta[l.cuenta] || l.concepto || "—"}
                            </TableCell>
                            <TableCell className="text-right font-mono tabular-nums text-xs">
                              {l.debe ? formatEur(l.debe) : "—"}
                            </TableCell>
                            <TableCell className="text-right font-mono tabular-nums text-xs">
                              {l.haber ? formatEur(l.haber) : "—"}
                            </TableCell>
                          </TableRow>
                        ))}
                        <TableRow className="bg-muted/40 font-medium">
                          <TableCell className="text-xs" colSpan={2}>
                            TOTAL ({asientoDetalle.lineas.length} líneas)
                          </TableCell>
                          <TableCell className="text-right font-mono tabular-nums text-xs">
                            {formatEur(asientoDetalle.total_debe)}
                          </TableCell>
                          <TableCell className="text-right font-mono tabular-nums text-xs">
                            {formatEur(asientoDetalle.total_haber)}
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </div>
                </CardContent>
              </Card>
            ) : (
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle>Diario de asientos</CardTitle>
                  <CardDescription>
                    Todos los asientos del año, incluidos los de cierre (marcados). Haz
                    clic en uno para verlo línea a línea.
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {diario === null || cargandoAsiento ? (
                    <div className="flex items-center justify-center h-24">
                      <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                    </div>
                  ) : (
                    <div className="rounded-md border overflow-x-auto">
                      <Table>
                        <TableHeader>
                          <TableRow className="bg-muted/30">
                            <TableHead className="w-[100px]">Fecha</TableHead>
                            <TableHead className="w-[90px]">Asiento</TableHead>
                            <TableHead>Concepto</TableHead>
                            <TableHead className="text-right w-[80px]">Líneas</TableHead>
                            <TableHead className="text-right w-[130px]">Debe</TableHead>
                            <TableHead className="text-right w-[130px]">Haber</TableHead>
                            <TableHead className="w-[30px]"></TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {(diario.asientos || []).map((g) => (
                            <TableRow
                              key={g.asiento}
                              className="hover:bg-muted/30 cursor-pointer"
                              onClick={() => abrirAsiento(g.asiento)}
                              data-testid={`libro-asiento-${g.asiento}`}
                            >
                              <TableCell className="text-xs">{g.fecha}</TableCell>
                              <TableCell className="text-xs tabular-nums">
                                {g.asiento ?? "—"}
                                {g.es_cierre && (
                                  <Badge variant="outline" className="ml-1 text-[10px]">cierre</Badge>
                                )}
                              </TableCell>
                              <TableCell className="text-xs max-w-[320px] truncate">
                                {g.concepto || "—"}
                              </TableCell>
                              <TableCell className="text-right tabular-nums text-xs">
                                {g.lineas}
                              </TableCell>
                              <TableCell className="text-right font-mono tabular-nums text-xs">
                                {formatEur(g.debe)}
                              </TableCell>
                              <TableCell className="text-right font-mono tabular-nums text-xs">
                                {formatEur(g.haber)}
                              </TableCell>
                              <TableCell>
                                <ChevronRight className="h-3.5 w-3.5 text-muted-foreground" />
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                      {diario.truncado && (
                        <p className="px-4 py-2 text-[11px] text-muted-foreground">
                          Mostrando los primeros {diario.asientos.length} de {diario.total}{" "}
                          asientos.
                        </p>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>
            )}
          </TabsContent>
        </Tabs>
      )}
    </div>
  );
}
